import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import {
    Business as BusinessIcon,
    DriveEta as DriveIcon,
    LocationOn as LocationIcon,
    Phone as PhoneIcon
} from '@mui/icons-material';
import type { SchoolDto } from '@/global/api/gen/ts/SchoolDto';
import type { SchoolLandingDto } from '@/global/api/gen/ts/SchoolLandingDto';

interface AutoSchoolCardProps {
    school: SchoolLandingDto;
}

export function AutoSchoolCard({school}: AutoSchoolCardProps) {
    const formatPhoneNumber = (phone: string) => {
        if (!phone) return '';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 11 && cleaned.startsWith('7')) {
            return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`;
        }
        return phone;
    };

    const getLocationString = (school: SchoolDto | SchoolLandingDto) => {
        const parts = [];
        if (school.locality) parts.push(school.locality);
        if (school.region && school.region !== school.locality) parts.push(school.region);
        return parts.join(', ');
    };

    const getAvailableCategories = (school: SchoolDto | SchoolLandingDto) => {
        return school.categories?.map(cat => cat.category_id) || [];
    };

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-2px)'
                },
                border: '1px solid #e9ecef'
            }}
        >
            <CardContent sx={{flexGrow: 1, p: 3}}>
                <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                        fontSize: {xs: '1rem', sm: '1.1rem'},
                        fontWeight: 'bold',
                        color: '#2c3e50',
                        mb: 2,
                        lineHeight: 1.3,
                        minHeight: '2.6rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}
                >
                    {school.title}
                </Typography>

                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2}}>
                    <LocationIcon sx={{color: '#6c757d', fontSize: '1.1rem', mt: 0.2}}/>
                    <Box sx={{flex: 1}}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#495057',
                                fontSize: '0.85rem',
                                lineHeight: 1.4
                            }}
                        >
                            {getLocationString(school)}
                        </Typography>
                        {school.address && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#6c757d',
                                    fontSize: '0.75rem',
                                    display: 'block',
                                    mt: 0.5
                                }}
                            >
                                {school.address}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box sx={{mb: 2}}>
                    {school.phone1 && (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                            <PhoneIcon sx={{color: '#28a745', fontSize: '1.1rem'}}/>
                            <Typography
                                variant="body2"
                                component="a"
                                href={`tel:+${school.phone1.replace(/\D/g, '')}`}
                                sx={{
                                    color: '#28a745',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {formatPhoneNumber(school.phone1)}
                            </Typography>
                        </Box>
                    )}

                    {school.phone2 && (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                            <PhoneIcon sx={{color: '#28a745', fontSize: '1.1rem'}}/>
                            <Typography
                                variant="body2"
                                component="a"
                                href={`tel:+${school.phone2.replace(/\D/g, '')}`}
                                sx={{
                                    color: '#28a745',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                {formatPhoneNumber(school.phone2)}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{mb: 2}}>
                    <Typography
                        variant="body2"
                        sx={{color: '#495057', fontSize: '0.85rem', mb: 1}}
                    >
                        <DriveIcon sx={{fontSize: '1rem', mr: 0.5, verticalAlign: 'middle'}}/>
                        Категории обучения:
                    </Typography>

                    <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        gap={0.5}
                    >
                        {getAvailableCategories(school).map(category => (
                            <Chip
                                key={category}
                                label={category}
                                size="small"
                                sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem',
                                    height: '24px'
                                }}
                            />
                        ))}
                        {getAvailableCategories(school).length === 0 && (
                            <Typography
                                variant="caption"
                                sx={{color: '#6c757d', fontStyle: 'italic'}}
                            >
                                Категории не указаны
                            </Typography>
                        )}
                    </Stack>
                </Box>

                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 2}}>
                    <BusinessIcon sx={{color: '#6c757d', fontSize: '1.1rem'}}/>
                    <Typography
                        variant="body2"
                        sx={{color: '#6c757d', fontSize: '0.8rem'}}
                    >
                        БИН:
                        {' '}
                        {school.bin}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}
